import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Calendar, ExternalLink, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const CAT_ICON = {
  pagamento: "💰",
  saude: "🏥",
  reuniao: "🤝",
  aniversario: "🎂",
  outro: "📌",
};

const CAT_COLOR = {
  pagamento: "border-l-amber-500",
  saude: "border-l-blue-500",
  reuniao: "border-l-purple-500",
  aniversario: "border-l-pink-500",
  outro: "border-l-gray-400",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function GoogleUpcomingCard() {
  const { apiCall } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall("get", "/integrations/google/upcoming-events?days_ahead=14");
      setEvents(res.data.events || []);
    } catch (err) {
      if (err.response?.status === 401) setError("Conecte o Google Calendar em Integrações");
      else setError("Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <Card className="p-4" data-testid="google-upcoming-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <h3 className="text-xs uppercase tracking-widest font-medium">Próximos 14 dias (Google Agenda)</h3>
        </div>
        <Button size="icon" variant="ghost" onClick={load} className="h-7 w-7" data-testid="google-refresh">
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground mb-3">
        Lembretes e compromissos cadastrados direto no seu Google Calendar — pagamentos, consultas, etc.
      </p>

      {error && (
        <div className="text-xs text-muted-foreground py-4 text-center">{error}</div>
      )}

      {!error && !loading && events.length === 0 && (
        <div className="text-xs text-muted-foreground py-4 text-center" data-testid="google-empty">
          Nada agendado para os próximos 14 dias.
        </div>
      )}

      <div className="space-y-1.5">
        {events.slice(0, 8).map((e) => (
          <a
            key={e.google_event_id}
            href={e.html_link}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-2 p-2 rounded-md bg-secondary/40 hover:bg-secondary border-l-2 ${CAT_COLOR[e.category] || CAT_COLOR.outro} transition`}
            data-testid={`google-event-${e.google_event_id}`}
          >
            <span className="text-base">{CAT_ICON[e.category] || CAT_ICON.outro}</span>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{e.title}</div>
              <div className="text-[10px] text-muted-foreground">{formatDate(e.start)}{e.location ? ` · ${e.location}` : ""}</div>
            </div>
            <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" strokeWidth={1.5} />
          </a>
        ))}
      </div>
      {events.length > 8 && (
        <div className="text-[10px] text-muted-foreground text-center mt-2">+ {events.length - 8} mais</div>
      )}
    </Card>
  );
}
